import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import head from 'lodash/head';
import debounce from 'lodash/debounce';
import { ui } from '@/constants';
import { GETTERS as CUSTOMER_GETTERS } from '@/store/customer';

import {
  MenuItemGroup,
  MenuMetadataForChannel,
  Menu,
  MealBuilderItemSection,
  BuilderMode,
  SelectableMenuItem,
  ItemCardCustomization,
  SelectableVariantMenuItem,
  MenuItemGroupType,
  OrderType,
  ItemCardSelectionInfoKind,
  BasicOrderMealItem,
  OrderMealItem,
  ItemCardCustomizationType,
  OrderErrorCode,
  OrderApiException,
  Restaurant,
  OrderNotFoundException,
  PreConfiguredMeal,
  PreConfiguredMealEntreeContent,
  PreConfiguredMealItem,
  DefaultMenuItemGroup,
  CustomerProfile,
  GroupedItemCustomization,
  OrderWithContext,
  OrderMealRequest,
  OrderMeal,
  MetadataTag,
  MealBuilderMenuSection,
  BuilderModeKind,
  MenuItemLookupResult,
  RecentOrFavoriteOrder,
} from '@/models';

import { ItemSelectedEventArgs } from '../CmgMealBuilderItemSelector/cmg-meal-builder-item-selector';
import { isDefined, slugify, findItemInMenu, isMenuItemRewardsExclusive, getRewardsExclusiveTagText } from '@/helpers';
import { Routes, isGroupOrderRoute } from '@/router';
import { GroupOrderErrorPageType } from '@/views/GroupOrder/GroupOrderError/cmg-group-order-error';
import { sortItemsByDeliveryEligiblity } from '@/services/meal-builder/utils';
import { AddToBagEvent, ErrorEvent, OpenBagEvent } from '@/services/analytics/events';
import { AnalyticsMealType, ProductFindingMethod } from '@/services/analytics/platforms/adobe-analytics/models';
import { AdobeCartAddEvent } from '@/services/analytics/platforms/adobe-analytics/adobe-events/adobe-cart-add';

@Component({
  name: 'CmgMealBuilder',
})
export default class CmgMealBuilder extends Vue {
  contentPaddingBottomPx = 0;
  selectedGroup: MenuItemGroup | null = null;
  isEditMode = false;
  editingMealName = '';
  loading = false;
  watchers: (() => void)[] = [];
  hasExclusiveItems = false;
  rewardsText = '';
  showDietaryFilterHint = false;
  toggleSectionValidationNavigation = false;

  private debouncedOnScroll = debounce(this.onScroll, 200);

  menuSections: MealBuilderMenuSection[] = [];
  sectionTitleHeight = 64;
  nutritionPrefHeight = 68;
  navTopOffset = 133 - this.sectionTitleHeight;
  entreeContainerTop = 0;

  @Prop({ required: true, type: Object })
  group!: MenuItemGroup;

  @Prop({ type: Object })
  selectedQuantityGroup?: MenuItemGroup;

  @Prop({ required: true, type: Object })
  menu!: Menu;

  @Prop({ required: true, type: Object })
  metadata!: MenuMetadataForChannel;

  @Prop({ required: false, type: String })
  mealId?: string;

  @Prop({ required: false, type: String })
  pcmId?: string;

  get hasMobileNav(): boolean {
    return this.$services.ui.isMobile();
  }

  get navIsVisible(): boolean {
    return (this.entreeContainerTop < (this.navTopOffset + this.nutritionPrefHeight) && this.hasMobileNav) ? true : false;
  }

  get addToBagButtonText(): string {
    if (this.isEditMode) {
      return 'SAVE';
    }

    return isGroupOrderRoute(this.$route) && this.$services.order.isCustomerParticipantOfGroupOrder() ? 'SUBMIT MY MEAL' : 'ADD TO BAG';
  }

  get nestedGroups(): MenuItemGroup[] | null {
    return this.group.type == MenuItemGroupType.Nested
      ? this.group.groups.map(groupInfo => this.metadata.groups.find(g => g.id == groupInfo.menuItemGroupId)).filter(isDefined)
      : null;
  }

  get quantityGroups(): MenuItemGroup[] | null {
    return this.group.type == MenuItemGroupType.Quantity
      ? this.group.groups.map(groupInfo => this.metadata.groups.find(g => g.id == groupInfo.menuItemGroupId)).filter(isDefined)
      : null;
  }

  get arrowRightImageUrl(): string {
    return this.$appStore.state.cms.images.icons.arrowRightGold.default;
  }

  get dietsAndAllergensSymbol(): string {
    return this.$appStore.state.cms.images.icons.nutritionFilter.default;
  }

  get itemSections(): MealBuilderItemSection[] {
    if (!this.builderMode) {
      return [];
    }

    return this.$services.mealBuilder.getItemSections(this.builderMode, this.variant).map(section => {
      const sectionMetadata = this.metadata.itemSections[section.id];
      const title = sectionMetadata?.alias ?? section.title;

      const result: MealBuilderItemSection = sectionMetadata
        ? {
            ...section,
            id: section.id,
            sortOrder: sectionMetadata.sortOrder,
            title,
            showSelectNone: sectionMetadata.hasSelectNoneOption,
          }
        : section;

      if (this.$services.order.isDelivery()) {
        result.items = result.items.sort(sortItemsByDeliveryEligiblity);
      }

      return result;
    }).sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
  }

  get visibleChurrosToast(): boolean {
    return this.$appStore.state.ui.toastVisibility;
  }

  get visibleItemSections(): MealBuilderItemSection[] {
    if (!this.builderMode) {
      return [];
    }

    return this.itemSections.filter(s => !s.hidden);
  }

  get builderMode(): BuilderMode | null {
    return this.$appStore.state.mealBuilder.builderMode;
  }

  get builderModeUI(): string {
    if (this.builderMode === null) {
      return '';
    }

    switch (this.builderMode.mode) {
      case BuilderModeKind.SingleEntreeType:
        return 'single-entree';
      case BuilderModeKind.MultiselectSingleEntreeType:
        return 'multiselect-single-entree';
      case BuilderModeKind.ZeroVariant:
        return 'zero-variant';
      default:
        return '';
    }
  }

  get selectedItems(): SelectableMenuItem[] {
    return this.$services.mealBuilder.getAllSelectedItems();
  }

  get variant(): SelectableVariantMenuItem | null {
    return this.$appStore.state.mealBuilder.selectedVariant ?? (this.builderMode && this.$services.mealBuilder.getDefaultVariant(this.builderMode)) ?? null;
  }

  get showFooter(): boolean {
    return !!this.selectedGroup;
  }

  get selectedRestaurant(): Restaurant | null {
    return this.$appStore.state.restaurant.selectedRestaurant;
  }

  get isEnrolledInLoyalty() {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get churrosImageUrl(): string {
    return this.$appStore.state.cms.images.menu.churros.default;
  }

  get churrosToastAlreadyDisplayed(): boolean {
    return this.$appStore.state.ui.churrosToastAlreadyDisplayed;
  }

  get isChurrosAvailableInMenu(): boolean {
    const churros = findItemInMenu('CMG-9004', this.menu); // CMG-9004 is the menu item ID for Churros
    return churros !== undefined;
  }

  get isKidsMeal(): boolean {
    return this.$router.currentRoute.params.groupName == `kid's-build-your-own` || this.$router.currentRoute.params.groupName == `kid's-quesadilla`;
  }

  get churrosToastDisplayPosition(): number {
    return this.$router.currentRoute.params.groupName == `sides-&-drinks` ? 300 : 2900;
  }

  get hasDietaryTagsSet(): boolean {
    return this.customer?.dietaryTags != null && this.customer.dietaryTags.length > 0;
  }

  get isDelivery(): boolean {
    return this.$services.order.isDelivery();
  }

  get recentOrders(): RecentOrFavoriteOrder[]  {
    return this.$appStore.getters[CUSTOMER_GETTERS.RECENT_ORDERS];
  }

  get menuItemType(): string | undefined {
    return this.group.menuItemType;
  }

  @Watch('quantityGroups', { immediate: true })
  async quantityGroupsChanged(group: MenuItemGroup) {
    this.refreshMealBuilderMode();
  }

  @Watch('isDelivery', { immediate: true })
  async isDeliveryChanged() {
    this.refreshMealBuilderMode();
  }
  
  @Watch('builderMode')
  builderModeChanged(mode: BuilderMode) {
    if(mode) {
      this.setMenuSections();
      this.$emit('builder-mode-changed');
    }
  }

  mounted() {
    this.refreshMealBuilderMode();

    this.watchers.push(this.$appStore.watch(state => state.menu.activeMenu, this.refreshMealBuilderMode));

    if (this.mealId) {
      this.setupEditMode();
    }

    if (this.pcmId) {
      this.setupPcmEditMode();
    }

    // If no restaurant has been selected yet, force the user to do so, but not for group order participants
    if (!this.selectedRestaurant && !isGroupOrderRoute(this.$route)) {
      this.$services.ui.setModalVisibility('findAChipotle', true, {
        onClose: () => {
          // If the user *still* hasn't selected a restaurant, nav backwards
          if (!this.selectedRestaurant) {
            this.$router.back();
          }
        },
      });
      this.$services.ui.setModalVisibility('toast', true, {
        body: 'Before you order, we gotta find your Chipotle first.'.toUpperCase(),
        bodyOnly: true,
      });
    }

    Vue.nextTick(() => {
      setTimeout(() => {
        document.getElementById(ui.componentIds.cmgAppContent)!.addEventListener('scroll', this.debouncedOnScroll);
      }, 1000);
    });

    if (!this.$appStore.state.ui.dietaryFilterHintShown && !this.customer?.dietaryTags) {
      this.showDietaryFilterHint = true;
      this.$services.ui.setDietaryFilterHintShown(true);
      setTimeout(() => {
        this.showDietaryFilterHint = false;
      }, 4000);
    }
    if (this.builderMode?.mode == BuilderModeKind.ZeroVariant && this.hasMobileNav) {
      window.addEventListener('scroll', this.calculateEntreeContainerTop);
      this.calculateEntreeContainerTop();
    }
  }

  destroyed() {
    this.$services.mealBuilder.resetBuilderMode();

    if (this.hasMobileNav) {
      window.removeEventListener('scroll', this.calculateEntreeContainerTop);
    }

    for (const unsubFn of this.watchers) {
      unsubFn();
    }
  }

  getRewardsText(tags: MetadataTag[]): string {
    return getRewardsExclusiveTagText(tags);
  }

  onScroll(e: any) {
    if (this.isChurrosAvailableInMenu && !this.churrosToastAlreadyDisplayed && !this.isKidsMeal && e.srcElement.scrollTop > this.churrosToastDisplayPosition) {
      this.$services.ui.setChurrosToastAlreadyDisplayed(true);
      this.$services.utils.targetView('view-churros');
    }
  }

  scrollToChurros() {
    const churrosEl = document.querySelectorAll('[data-qa-title="Churros"]');
    if (churrosEl) {
      // Navigate to the Desserts section title
      this.$nextTick(() => churrosEl[0].parentElement!.parentElement!.scrollIntoView());
    }
  }

  onSelectTags(event: any) {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    this.$services.ui.setModalVisibility('dietaryFiltersModal', true);
    this.$services.ui.firstFocus();
  }

  // Nested group contains Kids BYO and Kids Quesadilla
  isNestedGroupAvailable(group: DefaultMenuItemGroup): boolean {
    return this.$services.menu.isMenuItemTypeAvailable(group.menuItemType!);
  }

  isItemRewardsExclusive(group: MenuItemGroup): boolean {
    return isMenuItemRewardsExclusive(group.tags);
  }

  onItemSelected(args: ItemSelectedEventArgs, itemSection: MealBuilderItemSection) {
    if (!this.builderMode) {
      return;
    }

    this.$services.mealBuilder.selectItemSafe(args.item, itemSection, this.variant, args.args);
  }

  onItemCustomizationSelected(item: SelectableMenuItem, itemSection: MealBuilderItemSection, customization: ItemCardCustomization) {
    this.$services.mealBuilder.toggleItemCustomizationSafe(item, customization, itemSection, this.variant ? this.variant : undefined);
  }

  onSelectNestedGroup(group: MenuItemGroup) {
    const order = this.$services.order.getCurrentOrder();
    const isGroupOrder = order?.order.orderType == OrderType.Group;
    const isCustomerHostOfGroupOrder = this.$services.order.isCustomerHostOfGroupOrder();
    if (isGroupOrder && !isCustomerHostOfGroupOrder) {
      this.$router.push({ name: Routes.GroupOrderMealBuilder, params: { groupName: slugify(group.displayName), returnRoute: this.$route.path } });
    } else {
      this.$router.push({ name: Routes.MealBuilder, params: { groupName: slugify(group.displayName), returnRoute: this.$route.path } });
    }
    this.$services.ui.nextFocus('mealbuilder-header-focus');
  }

  onSelectQuantityGroup(group: MenuItemGroup, shouldTrackAnalytics?: boolean) {
    const previousVariant = this.$appStore.state.mealBuilder.selectedVariant;
    const previouslySelectedItems = this.selectedItems.map(i => ({ item: i, customizations: i.customizations.filter(c => c.selected) }));
    const previouslyOptedOutSections = [...this.$appStore.state.mealBuilder.optedOutItemSectionIds];

    // Refresh builder and set new group
    this.$services.mealBuilder.refreshBuilder(this.menu, this.metadata, group);
    this.selectedGroup = group;

    // Migrate variant selection
    const variants = (this.builderMode && this.$services.mealBuilder.getVariants(this.builderMode)) || [];
    const newVariant = (previousVariant && variants.find(v => v.item.primaryFillingName == previousVariant.item.primaryFillingName)) || null;

    if (newVariant && this.builderMode) {
      this.$services.mealBuilder.selectVariantSafe(this.builderMode, newVariant, null, variants, true, shouldTrackAnalytics);
    }

    // Migrate items
    const variantContents = this.variant?.contents ?? [];
    const itemsToSelect = previouslySelectedItems.map(i => ({
      old: i,
      new: variantContents.find((c: any) => c.item.itemId == i.item.item.itemId || c.item.itemName == i.item.item.itemName), // back up name check for Cauli Rice, Queso, and Guac
    }));

    for (const item of itemsToSelect) {
      if (!item.new) {
        continue;
      }

      this.$services.mealBuilder.selectItemSafe(item.new, null, this.variant, true, false, shouldTrackAnalytics);

      // Migrate customizations
      if (this.variant) {
        const customizationsToSelect = item.old.customizations
          .map((nc: any) => {
            if (nc.type != ItemCardCustomizationType.Customization || !item.new) {
              return null;
            }

            return item.new.customizations.find((oc: any) => oc.type == ItemCardCustomizationType.Customization && oc.customization.id == nc.customization.id);
          })
          .filter(isDefined);

        for (const customization of customizationsToSelect) {
          // Dont migrate a "Half" customization if its partner wasnt migrated
          if (customization.text === 'Half' && itemsToSelect.filter(filterItem => filterItem.new?.item.contentGroupName === item.new!.item.contentGroupName).length === 1) {
            continue;
          }
          this.$services.mealBuilder.toggleItemCustomizationSafe(item.new, customization, null, this.variant, false, shouldTrackAnalytics);
        }
      }
    }

    // Migrate opted-out sections
    const itemSectionsToOptOutOf = previouslyOptedOutSections.map(id => this.itemSections.find(i => i.id == id)).filter(isDefined);
    for (const section of itemSectionsToOptOutOf) {
      this.$services.mealBuilder.optOutOfItemSection(section, shouldTrackAnalytics);
    }
  }

  onRewardsExclusiveItemFound(rewardsText: string): void {
    this.rewardsText = rewardsText;
    this.hasExclusiveItems = true;
  }

  async onClickAddToBag() {
    // Check if online ordering flag was turned off in APIM
    await this.$services.featureFlags.validateOnlineOrdering();

    const builderMode = this.$appStore.state.mealBuilder.builderMode;
    if (!builderMode || !this.menu) {
      return;
    }

    // this page should be unreachable without selecting a Chipotle and creating an order but check just in case.
    const currentOrder = this.$services.order.getCurrentOrder();

    if (!this.selectedRestaurant && (!currentOrder || currentOrder.order.orderType != OrderType.Group)) {
      this.$services.ui.setModalVisibility('findAChipotle', true);

      this.$services.ui.setModalVisibility('toast', true, {
        body: 'Before you order, we gotta find your Chipotle first.'.toUpperCase(),
        bodyOnly: true,
      });
    }

    const validationError = this.$services.mealBuilder.validateMealBuilder(builderMode, this.metadata, this.itemSections);
    if (validationError.error) {
      this.toggleSectionValidationNavigation = !this.toggleSectionValidationNavigation;
      this.$services.analytics.send(new ErrorEvent('builder', validationError.error, 'add-to-bag'));
      return;
    }

    if (this.isEditMode && this.editingMealName) {
      // only showing spinner for edit mode because adding a new meal pops the meal name modal
      this.loading = true;
      try {
        await this.updateMeal(this.editingMealName, builderMode);
      } finally {
        this.loading = false;
      }
    } else {
      let preFilledMealName = '';
      if (this.$services.order.isCustomerParticipantOfGroupOrder()) {
        preFilledMealName = this.$appStore.state.order.groupOrderParticpantName;
      } else if (this.$appStore.state.customer.profile && !this.$appStore.state.customer.profile.isGuest) {
        preFilledMealName = this.$appStore.state.customer.profile.firstName;
      }

      this.$services.ui.setModalVisibility('mealBuilderMealName', true, {
        beforeClose: mealName => this.updateMeal(mealName, builderMode),
        mealName: preFilledMealName,
      });
      this.$services.ui.firstFocus();
    }
  }

  private async updateMeal(mealName: string, builderMode: BuilderMode) {
    let order = this.$services.order.getCurrentOrder();
    const isGroupOrder = order && order.order.orderType == OrderType.Group;

    try {
      if (!this.$appStore.state.customer.jwt && !isGroupOrder) {
        await this.$services.customer.createAutogeneratedUser();
      }

      if (!order) {
        if (!this.$appStore.state.restaurant.selectedRestaurant) {
          this.$services.logger.error('Somehow restaurant was deselected after popping "enter meal name" modal');
          return;
        }

        order = await this.$services.order.createOrder(this.$appStore.state.restaurant.selectedRestaurant.restaurantNumber, OrderType.Regular);
      }

      if (!order) {
        const error = 'currentOrder was somehow null after either checking it was truthy or creating a new order!';
        this.$services.logger.unreachable(error);

        throw new Error(error);
      }

      const createMealRequest = this.$services.mealBuilder.buildMealRequest(builderMode, this.selectedItems, mealName);
      if (!createMealRequest) {
        this.$services.logger.error('Failed to create a meal request to calculate meal cost');
        return;
      }

      if (this.isEditMode) {
        await this.$services.order.updateMealInOrder(order, this.mealId!, createMealRequest);
      } else {
        await this.$services.order.addMealToOrder(order, createMealRequest);
        this.$services.googleAnalytics.builtMealAddedToBag(createMealRequest);
        this.sendAnalyticsEvents(order, createMealRequest);
      }
    } catch (e) {
      if (e instanceof OrderNotFoundException && isGroupOrder) {
        // The most likely scenario for not found is because a host cancelled the Group Order
        this.$services.order.setPendingGroupOrder(null, '');
        this.$router.push({ name: Routes.GroupOrderError, params: { type: GroupOrderErrorPageType.NotFound } });
        return;
      } else if (e instanceof OrderApiException && e.errors.length > 0) {
        const error = e.errors[0];

        if (error) {
          if (isGroupOrder && error.errorCode == OrderErrorCode.OrderCompleted) {
            this.$services.bag.setBagVisibility(false);
            this.$router.push({ name: Routes.GroupOrderError, params: { type: GroupOrderErrorPageType.NotFound } });
            return;
          }

          if (isGroupOrder && error.errorCode == OrderErrorCode.MaxGroupParticipantsExceeded) {
            this.$services.bag.setBagVisibility(false);
            this.$router.push({ name: Routes.GroupOrderError, params: { type: GroupOrderErrorPageType.MaxParticipantCountReached } });
            return;
          }

          // TODO-6cyqjh: error code mapping
          this.$services.ui.setModalVisibility('mealBuilderMealName', false);

          throw e;
        }
      }

      this.$services.logger.error('Something went really wrong when adding item to bag: ', undefined, e);
      this.$services.ui.setModalVisibility('globalErrorToast', true);
    }

    if (this.$services.order.isCustomerParticipantOfGroupOrder()) {
      this.$router.replace({ name: Routes.GroupOrderConfirmation });
    } else {
      this.$services.bag.setBagVisibility(true);
      this.$services.ui.setLastSelectedElement('bag');
      Vue.nextTick(() => this.$services.ui.focusDiv('.fullModal'));
      this.$router.replace({ name: Routes.Home });
    }
  }

  private sendAnalyticsEvents(order: OrderWithContext, createMealRequest: OrderMealRequest): void {
    const meal = createMealRequest.meal as OrderMeal;

    // Tracking if side or drink is ordered as part of a meal (addon) or from the toplevel sides and drinks section (standalone)
    const productFindingMethod = (meal.drinks?.length || meal.sides?.length) ? meal.entrees?.length ? ProductFindingMethod.AddOn : ProductFindingMethod.Standalone : undefined;
    this.$services.analytics.setAddToBagEventToStore(new AdobeCartAddEvent(new AddToBagEvent(meal, AnalyticsMealType.Standard, productFindingMethod), this.$appStore, this.$services));
  }

  private refreshMealBuilderMode() {
    if (!this.menu || !this.metadata || !this.group) {
      return;
    }

    switch (this.group.type) {
      case MenuItemGroupType.Default:
        this.$services.mealBuilder.refreshBuilder(this.menu, this.metadata, this.group);
        this.selectedGroup = this.group;
        break;

      case MenuItemGroupType.Nested:
        break;

      case MenuItemGroupType.Quantity:
        const group = this.selectedQuantityGroup || (this.quantityGroups || [])[0];
        if (group) {
          this.onSelectQuantityGroup(group, false);
        }
        
        break;
    }
  }

  private async setupPcmEditMode(): Promise<void> {
    const meal = head(this.$appStore.state.menu.preConfiguredMeals.filter((m: PreConfiguredMeal) => m.mealId == this.pcmId));

    if (!meal) {
      return;
    }

    await this.$services.menu.waitForRestaurantMenuLoad();

    this.editingMealName = meal.mealName;

    // first top level menuItemId will determine which variant to use
    const editedMenuItemId = this.$services.menu.getFirstTopLevelMenuItemIdForPcm(meal);
    const selectedVariant = this.$appStore.state.mealBuilder.displayedVariants.find(v => v.item.itemId == editedMenuItemId) || null;

    const menuItems = ((meal.entree.contents as PreConfiguredMealItem[]) || []).concat(meal.sides || []).concat(meal.drinks || []);

    // Select variant
    if (selectedVariant && this.builderMode) {
      this.$services.mealBuilder.selectVariantSafe(this.builderMode, selectedVariant, null, this.$appStore.state.mealBuilder.displayedVariants, null, false);
    }

    // select items within each item section
    const itemSections = this.itemSections;
    for (const section of itemSections) {
      for (const selectableItem of section.items) {
        const menuItem = menuItems.find((x: PreConfiguredMealItem) => x.itemId == selectableItem.item.itemId && x.isItemAvailable);
        if (!menuItem) {
          continue;
        }

        const args = selectableItem.selectionInfo.kind == ItemCardSelectionInfoKind.Quantity ? menuItem.quantity : true;
        this.$services.mealBuilder.selectItemSafe(selectableItem, section, selectedVariant, args, undefined, false);

        if (selectedVariant) {
          // currently all customizations require a variant because MaxQuantity is at the variant level, not the item level
          if ((menuItem as PreConfiguredMealEntreeContent).customizationId) {
            const customization = selectableItem.customizations.find(
              c => c.type == ItemCardCustomizationType.Customization && c.customization.id == (menuItem as PreConfiguredMealEntreeContent).customizationId,
            );

            // If the item with a customization was already selected above (e.g. half rice), then toggling would deselect, which is not what we want
            if (customization && !customization.selected) {
              this.$services.mealBuilder.toggleItemCustomizationSafe(selectableItem, customization, section, selectedVariant, undefined, false);
            }
          }

          // Handle variant customizations
          for (const variantCustomization of selectedVariant.customizations) {
            if (variantCustomization.type == ItemCardCustomizationType.SelectableItem && variantCustomization.portion == selectableItem.item.itemType) {
              variantCustomization.selected = true;
            }
          }
        }
      }

      if (!this.$services.mealBuilder.getSelectedItems(section.items).length && section.showSelectNone) {
        this.$services.mealBuilder.optOutOfItemSection(section, false);
      }
    }
  }

  private setupEditMode(): void {
    const order = this.$services.order.getCurrentOrder();
    if (!order || !order.order.meals) {
      return;
    }

    const meal = order.order.meals.find(m => m.mealId == this.mealId);
    if (!meal) {
      return;
    }

    this.isEditMode = true;
    this.editingMealName = meal.mealName;

    // first top level menuItemId will determine which variant to use
    const editedMenuItemId = this.$services.menu.getFirstTopLevelMenuItemId(meal);
    const selectedVariant = this.$appStore.state.mealBuilder.displayedVariants.find(v => v.item.itemId == editedMenuItemId) ?? null;

    // Currently we only support one entree per meal
    const entreeContents = meal.entrees && meal.entrees.length > 0 && meal.entrees[0].contents ? (meal.entrees[0].contents as OrderMealItem[]) : [];
    const menuItems = entreeContents.concat(meal.sides ?? []).concat(meal.drinks ?? []);

    // Select variant
    if (selectedVariant && this.builderMode) {
      this.$services.mealBuilder.selectVariantSafe(this.builderMode, selectedVariant, null, this.$appStore.state.mealBuilder.displayedVariants, null, false);
    }

    // select items within each item section
    const itemSections = this.itemSections;
    for (const section of itemSections) {
      for (const selectableItem of section.items) {
        const menuItem = selectableItem.groupedItemSelectionOptions
          ? menuItems.find((x: BasicOrderMealItem) => selectableItem.groupedItemSelectionOptions?.map(groupedItem => groupedItem.id).includes(x.menuItemId))
          : menuItems.find((x: BasicOrderMealItem) => x.menuItemId === selectableItem.item.itemId);

        if (!menuItem) {
          continue;
        }

        // Handle editing grouped items that aren't the default "customization" selection, e.g. CMG-1039 extra cauli rice or 32 fl oz fountain beverage
        const groupedItemCustomization = selectableItem.groupedItemSelectionOptions?.find(
          groupedItem => groupedItem.id === menuItem?.menuItemId && selectableItem.defaultItemId !== menuItem.menuItemId,
        );
        if (groupedItemCustomization) {
          const customization = {
            customization: {
              unitPrice: groupedItemCustomization?.price,
              unitDeliveryPrice: groupedItemCustomization?.deliveryPrice,
              selectedText: groupedItemCustomization?.showCustomizationNameWhenSelected ? groupedItemCustomization.asCustomizationName : '',
              ...groupedItemCustomization,
            } as GroupedItemCustomization,
            item: selectableItem,
            hidden: false, // So far there are no hidden grouped item customizations (thank goodness), but if anyone ever conjures such a monstrosity, this will need to change
            iconText: selectableItem.displayNameForGroupedItems ?? '',
            text: selectableItem.groupedItemSelectionOptions?.find(groupedItem => groupedItem.id === selectableItem.item.itemId)?.asCustomizationName ?? '',
            type: ItemCardCustomizationType.SelectableItem as ItemCardCustomizationType.SelectableItem,
            selected: false,
          };
          this.$services.mealBuilder.toggleItemCustomizationSafe(selectableItem, customization, null, selectedVariant ?? undefined, false, false);
          // Handle correct quantity selection (e.g. tractor bevvies and fountain bevvies)
          const quantity = selectableItem.selectionInfo.kind == ItemCardSelectionInfoKind.Quantity ? menuItem.quantity : true;
          if (quantity) {
            this.$services.mealBuilder.selectItemSafe(selectableItem, section, selectedVariant, quantity, undefined, false);
          }
          continue;
        }

        const args = selectableItem.selectionInfo.kind == ItemCardSelectionInfoKind.Quantity ? menuItem.quantity : true;
        this.$services.mealBuilder.selectItemSafe(selectableItem, section, selectedVariant, args, undefined, false);

        if (selectedVariant) {
          // currently all customizations require a variant because MaxQuantity is at the variant level, not the item level
          if (menuItem.customizationId) {
            const customization = selectableItem.customizations.find(c => c.type == ItemCardCustomizationType.Customization && c.customization.id == menuItem.customizationId);

            // If the item with a customization was already selected above (e.g. half rice), then toggling would deselect, which is not what we want
            if (customization && !customization.selected) {
              this.$services.mealBuilder.toggleItemCustomizationSafe(selectableItem, customization, section, selectedVariant, undefined, false);
            }
          }

          // Handle variant customizations
          for (const variantCustomization of selectedVariant.customizations) {
            if (variantCustomization.type == ItemCardCustomizationType.SelectableItem && variantCustomization.portion == selectableItem.item.itemType) {
              variantCustomization.selected = true;
            }
          }
        }
      }

      if (!this.$services.mealBuilder.getSelectedItems(section.items).length && section.showSelectNone) {
        this.$services.mealBuilder.optOutOfItemSection(section, false);
      }
    }
  }

  private setMenuSections() {  
    this.menuSections = [];
    let flatRefs = this.visibleItemSections.map((e) => this.$refs[e.title]).flat();

    this.visibleItemSections.map((e,i) => {
      this.menuSections.push({title: e.title, elementRef: flatRefs[i] as HTMLElement});
    })
  }

  private calculateEntreeContainerTop() {
    const mbc = this.$refs.mealBuilderContainer;
    this.entreeContainerTop = (mbc) ? (mbc as HTMLElement).getBoundingClientRect().top : 0;
  }
}
